import { DRAWER_WIDTH } from './chunk-CUKHRH2F.js';
import { __spreadValues } from './chunk-2FEJBM4N.js';
import React5, { forwardRef, useRef, useState, useCallback, useEffect } from 'react';
import { TextField, InputAdornment, Box, Typography, CircularProgress, List, Skeleton, Dialog, MenuItem, Icon } from '@mui/material';
import { Search } from '@mui/icons-material';

var SearchEmptyState = ({
  title = "No results found",
  description = "Try using a different search term.",
  imageSrc,
  sx
}) => {
  return /* @__PURE__ */ React5.createElement(Box, { display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 2, height: "150px", width: "100%", sx }, imageSrc && /* @__PURE__ */ React5.createElement("img", { src: imageSrc, alt: "search-placeholder", width: "60px", height: "60px" }), /* @__PURE__ */ React5.createElement(Box, { textAlign: "center" }, /* @__PURE__ */ React5.createElement(Typography, { fontSize: 13, color: "#000A", px: 1 }, title), description && /* @__PURE__ */ React5.createElement(Typography, { fontSize: 12, color: "#88949B", px: 1 }, description)));
};
var SearchEmptyState_component_default = SearchEmptyState;
var SearchInput = forwardRef(
  ({ value, onChange, loading = false, placeholder = "Search...", autoFocus = true, keyboardHint = "Esc to close", sx }, ref) => {
    return /* @__PURE__ */ React5.createElement(
      TextField,
      {
        inputRef: ref,
        value,
        autoFocus,
        sx: __spreadValues({
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
            borderBottom: "1px solid #E6E6E6 !important"
          },
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderRadius: 0
            }
          },
          fontWeight: 600
        }, sx),
        onChange: (e) => onChange(e.target.value),
        InputProps: {
          sx: {
            p: 2,
            fontSize: 14
          },
          inputProps: {
            sx: {
              p: 0
            }
          },
          startAdornment: /* @__PURE__ */ React5.createElement(InputAdornment, { position: "start" }, loading ? /* @__PURE__ */ React5.createElement(CircularProgress, { size: 20 }) : /* @__PURE__ */ React5.createElement(Search, { sx: { width: 20 } })),
          endAdornment: keyboardHint ? /* @__PURE__ */ React5.createElement(InputAdornment, { position: "end" }, /* @__PURE__ */ React5.createElement(
            Box,
            {
              sx: {
                py: "2px",
                px: "4px",
                bgcolor: "#F0F0F0",
                border: "1px solid #C0C0C0",
                borderRadius: "4px",
                m: 1
              }
            },
            /* @__PURE__ */ React5.createElement(Typography, { fontSize: 11, fontWeight: 400, color: "#0008" }, keyboardHint)
          )) : void 0
        },
        placeholder,
        fullWidth: true
      }
    );
  }
);
SearchInput.displayName = "SearchInput";
var SearchInput_component_default = SearchInput;
var SearchListSkeleton = ({ count = 4 }) => {
  return /* @__PURE__ */ React5.createElement(List, { sx: { p: 0, gap: "4px", display: "flex", flexDirection: "column", width: "100%" } }, [...Array(count)].map((_, index) => /* @__PURE__ */ React5.createElement(
    MenuItem,
    {
      key: `skeleton-item-${index}`,
      sx: {
        display: "flex",
        alignItems: "center",
        gap: 2,
        height: "42px",
        px: 1,
        borderRadius: "4px",
        backgroundColor: "rgba(0, 0, 0, 0.02)",
        cursor: "default"
      }
    },
    /* @__PURE__ */ React5.createElement(
      Skeleton,
      {
        variant: "rounded",
        width: 22,
        height: 22,
        sx: {
          minWidth: "22px",
          minHeight: "22px"
        }
      }
    ),
    /* @__PURE__ */ React5.createElement(Box, { width: "100%" }, /* @__PURE__ */ React5.createElement(Skeleton, { variant: "rounded", width: "60%", height: 16 }), /* @__PURE__ */ React5.createElement(Skeleton, { variant: "rounded", width: "40%", height: 13, sx: { mt: "2px" } }))
  )));
};
var SearchListItem = ({
  item,
  index,
  isSelected,
  onSelect
}) => {
  const renderIcon = () => {
    if (!item.icon) return /* @__PURE__ */ React5.createElement(Icon, { sx: { fontSize: "22px" } }, "article");
    if (typeof item.icon === "string") {
      return /* @__PURE__ */ React5.createElement(Icon, { sx: { fontSize: "22px" } }, item.icon);
    }
    return item.icon;
  };
  return /* @__PURE__ */ React5.createElement(
    MenuItem,
    {
      onClick: () => onSelect(item, index),
      sx: {
        display: "flex",
        alignItems: "center",
        gap: 2,
        height: "42px",
        px: 1,
        borderRadius: "4px",
        transition: "ease 0.15s",
        backgroundColor: isSelected ? "rgba(25, 110, 130, 0.08)" : "rgba(0, 0, 0, 0.02)",
        borderLeft: isSelected ? "3px solid #196E82" : "3px solid transparent",
        "&:hover": {
          backgroundColor: "rgba(25, 110, 130, 0.08)"
        }
      }
    },
    renderIcon(),
    /* @__PURE__ */ React5.createElement(Box, { sx: { overflow: "hidden", flex: 1 } }, /* @__PURE__ */ React5.createElement(Typography, { fontWeight: 400, fontSize: 13, color: "#000", lineHeight: "initial", noWrap: true }, item.parentLabel ? `${item.parentLabel} - ${item.title}` : item.title), item.category && /* @__PURE__ */ React5.createElement(Typography, { fontSize: 11, color: "#88949B", lineHeight: "initial", noWrap: true }, item.category))
  );
};
var SearchList = ({ items, onItemSelect, loading = false, loadingCount = 4, selectedIndex, sx }) => {
  if (loading) {
    return /* @__PURE__ */ React5.createElement(SearchListSkeleton, { count: loadingCount });
  }
  if (!items || items.length === 0) {
    return null;
  }
  return /* @__PURE__ */ React5.createElement(List, { sx: __spreadValues({ p: 0, gap: "4px", display: "flex", flexDirection: "column", width: "100%" }, sx) }, items.map((item, index) => /* @__PURE__ */ React5.createElement(
    SearchListItem,
    {
      key: item.id || `${item.title}-${index}`,
      item,
      index,
      isSelected: selectedIndex === index,
      onSelect: onItemSelect
    }
  )));
};
var SearchList_component_default = SearchList;
var SearchTitle = ({ title, loading = false, sx }) => {
  return /* @__PURE__ */ React5.createElement(Box, { width: "100%", borderRadius: "2px", mb: 1, sx }, loading ? /* @__PURE__ */ React5.createElement(Skeleton, { variant: "rounded", width: "40%", height: "25px" }) : /* @__PURE__ */ React5.createElement(Typography, { fontSize: 13, color: "#122937", fontWeight: 600, py: "4px", lineHeight: "initial" }, title));
};
var SearchTitle_component_default = SearchTitle;

// src/components/SearchDialog/SearchDialog.component.tsx
var DEFAULT_DRAWER_WIDTH = DRAWER_WIDTH.OPEN;
var DEFAULT_DRAWER_CLOSE_WIDTH = DRAWER_WIDTH.CLOSE;
var SearchDialog = ({
  open,
  onClose,
  searchValue,
  onSearchChange,
  results,
  loading = false,
  recentItems,
  recentItemsLoading = false,
  onItemSelect,
  placeholder = "Search...",
  emptyStateTitle = "Start typing to see results",
  emptyStateDescription,
  noResultsText = "No results found",
  recentItemsTitle = "Recently Visited",
  resultsTitle = "Results",
  drawerWidth = DEFAULT_DRAWER_WIDTH,
  drawerOpen = true,
  minSearchLength = 3,
  sx
}) => {
  const inputRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const currentDrawerWidth = drawerOpen ? drawerWidth : DEFAULT_DRAWER_CLOSE_WIDTH;
  const handleClose = useCallback(() => {
    onClose();
    setTimeout(() => {
      onSearchChange("");
      setSelectedIndex(0);
    }, 300);
  }, [onClose, onSearchChange]);
  const handleItemSelect = useCallback(
    (item, index) => {
      onItemSelect(item, index);
      handleClose();
    },
    [onItemSelect, handleClose]
  );
  const handleKeyDown = useCallback(
    (event) => {
      const items = searchValue.length >= minSearchLength ? results : recentItems || [];
      const maxIndex = items.length - 1;
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setSelectedIndex((prev) => prev >= maxIndex ? 0 : prev + 1);
          break;
        case "ArrowUp":
          event.preventDefault();
          setSelectedIndex((prev) => prev <= 0 ? maxIndex : prev - 1);
          break;
        case "Enter":
          event.preventDefault();
          if (items[selectedIndex]) {
            handleItemSelect(items[selectedIndex], selectedIndex);
          }
          break;
        case "Escape":
          event.preventDefault();
          handleClose();
          break;
      }
    },
    [searchValue, minSearchLength, results, recentItems, selectedIndex, handleItemSelect, handleClose]
  );
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        var _a;
        (_a = inputRef.current) == null ? void 0 : _a.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [open]);
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchValue, results]);
  const hasSearch = searchValue.length >= minSearchLength;
  const hasResults = results && results.length > 0;
  const hasRecentItems = recentItems && recentItems.length > 0;
  const showEmptyState = !hasSearch && !hasRecentItems;
  const showNoResults = hasSearch && !hasResults && !loading;
  const showResults = hasSearch && hasResults;
  const showRecentItems = !hasSearch && hasRecentItems;
  return /* @__PURE__ */ React5.createElement(
    Dialog,
    {
      open,
      onClose: handleClose,
      disableScrollLock: true,
      onKeyDown: handleKeyDown,
      PaperProps: {
        sx: __spreadValues({
          borderRadius: "10px",
          width: `calc(100% - ${currentDrawerWidth + 60}px)`,
          position: "absolute",
          left: `${currentDrawerWidth}px`,
          top: "0px",
          maxWidth: "100%"
        }, sx)
      }
    },
    /* @__PURE__ */ React5.createElement(Box, null, /* @__PURE__ */ React5.createElement(
      SearchInput_component_default,
      {
        ref: inputRef,
        value: searchValue,
        onChange: onSearchChange,
        loading,
        placeholder,
        keyboardHint: "Esc to close"
      }
    ), /* @__PURE__ */ React5.createElement(Box, { p: 2, display: "flex", flexDirection: "column", gap: 1 }, showEmptyState && /* @__PURE__ */ React5.createElement(SearchEmptyState_component_default, { title: emptyStateTitle, description: emptyStateDescription }), showNoResults && /* @__PURE__ */ React5.createElement(Box, null, /* @__PURE__ */ React5.createElement(
      SearchEmptyState_component_default,
      {
        title: noResultsText,
        description: `No results for "${searchValue}". Try a different search term.`
      }
    ), hasRecentItems && /* @__PURE__ */ React5.createElement(Box, { mt: 2 }, /* @__PURE__ */ React5.createElement(SearchTitle_component_default, { title: recentItemsTitle, loading: recentItemsLoading }), /* @__PURE__ */ React5.createElement(
      SearchList_component_default,
      {
        items: recentItems.slice(0, 4),
        onItemSelect: handleItemSelect,
        loading: recentItemsLoading,
        selectedIndex
      }
    ))), showResults && /* @__PURE__ */ React5.createElement(Box, null, /* @__PURE__ */ React5.createElement(SearchTitle_component_default, { title: resultsTitle }), /* @__PURE__ */ React5.createElement(SearchList_component_default, { items: results, onItemSelect: handleItemSelect, loading, selectedIndex })), showRecentItems && /* @__PURE__ */ React5.createElement(Box, null, /* @__PURE__ */ React5.createElement(SearchTitle_component_default, { title: recentItemsTitle, loading: recentItemsLoading }), /* @__PURE__ */ React5.createElement(
      SearchList_component_default,
      {
        items: recentItems.slice(0, 4),
        onItemSelect: handleItemSelect,
        loading: recentItemsLoading,
        selectedIndex
      }
    ))))
  );
};
var SearchDialog_component_default = SearchDialog;

export { SearchDialog_component_default, SearchEmptyState_component_default, SearchInput_component_default, SearchList_component_default, SearchTitle_component_default };
//# sourceMappingURL=chunk-QQ23VFRA.js.map
//# sourceMappingURL=chunk-QQ23VFRA.js.map